import type {
  IReferenceWellsFullInfoApi,
  TReferenceDesignCatalogItem,
  TReferenceWellboreCatalogItem,
  TWellChildrenCatalog,
} from '@repo/reference-wells';

import { dashboardServiceAgent } from 'src/api/dashboard-service-agent';
import { throwApiError } from 'src/api/utils';

type TRawTrajectory = {
  id: number;
  name: string;
  isActive: boolean;
};

type TRawWellbore = {
  id: number;
  name: string;
  redrillingIndex: number | null;
  typeIndex: number;
  typeSlug: string;
  isActive: boolean;
  trajectories: TRawTrajectory[];
};

type TRawDesign = {
  id: number;
  name: string;
  isActive: boolean;
  wellbores: TRawWellbore[];
};

type TRawWellFullInfo = {
  id: number;
  name: string;
  fact?: {
    wellbores?: TRawWellbore[];
  };
  plan?: {
    designs?: TRawDesign[];
  };
};

function mapWellbore(raw: TRawWellbore): TReferenceWellboreCatalogItem {
  return {
    id: raw.id,
    name: raw.name,
    redrillingIndex: raw.redrillingIndex ?? null,
    typeIndex: raw.typeIndex,
    typeSlug: raw.typeSlug,
    isActive: Boolean(raw.isActive),
    trajectories: (raw.trajectories ?? []).map((trajectory) => ({
      id: trajectory.id,
      name: trajectory.name,
      isActive: Boolean(trajectory.isActive),
    })),
  };
}

function mapDesign(raw: TRawDesign): TReferenceDesignCatalogItem {
  return {
    id: raw.id,
    name: raw.name,
    isActive: Boolean(raw.isActive),
    wellbores: (raw.wellbores ?? []).map(mapWellbore),
  };
}

function mapWellFullInfo(raw: TRawWellFullInfo): TWellChildrenCatalog {
  return {
    factWellbores: (raw.fact?.wellbores ?? []).map(mapWellbore),
    planDesigns: (raw.plan?.designs ?? []).map(mapDesign),
  };
}

/**
 * Per-instance API client for `/well/all/full-info`.
 * TODO: replace with a shared request cache / dedupe layer between widget instances.
 */
export class ReferenceWellsFullInfoApi implements IReferenceWellsFullInfoApi {
  async fetchChildren(): Promise<Map<number, TWellChildrenCatalog>> {
    try {
      const { data } = await dashboardServiceAgent.get<TRawWellFullInfo[]>('well/all/full-info');
      const map = new Map<number, TWellChildrenCatalog>();

      for (const well of data ?? []) {
        map.set(well.id, mapWellFullInfo(well));
      }

      return map;
    } catch (error) {
      throwApiError(error);
    }
  }
}
